<template>
	<div class="command-categories" style="height: 116px; min-height: 116px; max-height: 116px">
		<div class="category">
			<div class="category-actions">
				<DropdownButton
					v-if="!isSketchActiveComputed"
					:disabled="isSketchMirrorEntitiesActive"
					:is-active="isSketchEntryButtonActive"
					:icon="sketchIcon"
					label="草图绘制"
					:hint="{
						label: '草图绘制',
						desc: '创建新草图，或编辑现有草图。',
					}"
					size="large"
					:options="[
						{ label: '草图绘制', key: 'sketch-enter' },
						{ label: '3D 草图', key: 'sketch-3d', disabled: true },
					]"
					@select="handleSketchMenuSelect"
					@click="handleSketchEntryClick"
				/>

				<DropdownButton
					v-else
					:disabled="isSketchMirrorEntitiesActive"
					:is-active="isSketchEntryButtonActive"
					:icon="sketchExitIcon"
					label="退出草图"
					:hint="{
						label: '退出草图',
						desc: '退出此草图并保留任何更改。',
					}"
					size="large"
					:options="[
						{ label: '草图绘制', key: 'sketch-enter' },
						{ label: '3D 草图', key: 'sketch-3d', disabled: true },
					]"
					@select="emit('command', 'sketch-exit')"
					@click="emit('command', 'sketch-exit')"
				/>

				<DropdownButton
					:disabled="!isSketchActiveComputed || isSketchMirrorEntitiesActive"
					:is-active="SMART_DIMENSION_FAMILY_COMMANDS.some(command => activeCommands.includes(command))"
					:icon="intelligentSizeIcon"
					label="智能尺寸"
					:hint="{
						label: '智能尺寸',
						image: '/images/operation/sketch-smart-dimension.gif',
						desc: '依次选择草图实体，移动指针切换候选方向，右键锁定，单击空白处放置，输入数值进行确认。',
					}"
					size="large"
					:options="[
						{ label: '智能尺寸', key: 'sketch-smart-dimension' },
						{ label: '水平尺寸', key: 'sketch-horizontal-dimension' },
						{ label: '竖直尺寸', key: 'sketch-vertical-dimension' },
						{ label: '路径长度', key: 'sketch-path-length-dimension' },
						{ label: '链尺寸', key: 'sketch-chain-dimension' },
						{ label: '基准尺寸', key: 'sketch-baseline-dimension' },
						{ label: '尺寸链', key: 'sketch-ordinate-dimension' },
						{ label: '水平尺寸链', key: 'sketch-horizontal-ordinate-dimension' },
						{ label: '竖直尺寸链', key: 'sketch-vertical-ordinate-dimension' },
					]"
					@select="(key: string) => emit('command', key)"
					@click="emit('command', 'sketch-smart-dimension')"
				/>
			</div>
		</div>

		<div
			v-for="category in sketchCategories"
			:key="category.title"
			class="category"
			:class="{ 'category-draw': category.title === '绘制' }"
			:data-sketch-category="category.title"
		>
			<div v-if="hasSketchLayout(category)" class="category-actions sketch-layout-grid" :style="getSketchGridStyle(category)">
				<div
					v-for="item in getSketchLayoutItems(category)"
					:key="item.action.key"
					class="sketch-command"
					:class="{
						active: isSketchLayoutActive(item.action),
						'sketch-command-large': item.size === 'large',
						'sketch-command-small': item.size === 'small',
					}"
					:style="getSketchCellStyle(item)"
					:data-sketch-key="item.action.key"
					:data-sketch-disabled="isSketchLayoutDisabled(item.action) ? '1' : '0'"
					:data-sketch-size="item.size"
					:data-sketch-mode="item.mode"
					:data-sketch-row-span="String(item.rowSpan)"
				>
					<DropdownButton
						:is-active="isSketchLayoutActive(item.action)"
						:disabled="isSketchLayoutDisabled(item.action)"
						:icon="getSketchLayoutIcon(item.action)"
						:label="getSketchLayoutLabel(item.action)"
						:hint="{
							label: getSketchLayoutLabel(item.action),
							image: item.action.hintImage,
							desc: item.action.hint,
						}"
						:size="item.size === 'large' ? 'large' : 'small'"
						:direction="item.mode"
						:options="sketchLayoutOptionsMap[item.action.key] ?? []"
						@select="handleSketchLayoutSelect(item.action.key, $event)"
						@click="handleSketchLayoutClick(item.action)"
					/>
				</div>
			</div>

			<div v-else class="category-actions">
				<template v-for="action in category.actions" :key="action.key">
					<div v-if="action.key === 'rectangle'" class="command-split" :class="{ active: isRectangleActive }" :data-sketch-key="action.key">
						<DropdownButton
							:is-active="isRectangleActive"
							:disabled="isSketchActionLockedByMirror(action.key)"
							:icon="activeRectangleOption.icon"
							:hint="{
								label: activeRectangleOption.label,
								image: activeRectangleOption.hintImage,
								desc: activeRectangleOption.hint,
							}"
							size="small"
							direction="row"
							:options="rectangleDropdownOptions"
							@select="handleRectangleSelect"
							@click="handleRectangleClick"
						/>
					</div>

					<div v-else-if="action.key === 'line'" class="command-split" :class="{ active: isLineActive }" :data-sketch-key="action.key">
						<DropdownButton
							:is-active="isLineActive"
							:disabled="isSketchActionLockedByMirror(action.key)"
							:icon="activeLineOption.icon"
							:hint="{
								label: activeLineOption.label,
								image: activeLineOption.hintImage,
								desc: activeLineOption.hint,
							}"
							size="small"
							direction="row"
							:options="lineDropdownOptions"
							@select="handleLineSelect"
							@click="handleLineClick"
						/>
					</div>

					<div v-else-if="action.key === 'arc'" class="command-split" :class="{ active: isArcActive }" :data-sketch-key="action.key">
						<DropdownButton
							:is-active="isArcActive"
							:disabled="isSketchActionLockedByMirror(action.key)"
							:icon="activeArcOption.icon"
							:hint="{
								label: activeArcOption.label,
								image: activeArcOption.hintImage,
								desc: activeArcOption.hint,
							}"
							size="small"
							direction="row"
							:options="arcDropdownOptions"
							@select="handleArcSelect"
							@click="handleArcClick"
						/>
					</div>

					<div v-else-if="action.key === 'circle'" class="command-split" :class="{ active: isCircleActive }" :data-sketch-key="action.key">
						<DropdownButton
							:is-active="isCircleActive"
							:disabled="isSketchActionLockedByMirror(action.key)"
							:icon="activeCircleOption.icon"
							:hint="{
								label: activeCircleOption.label,
								image: activeCircleOption.hintImage,
								desc: activeCircleOption.hint,
							}"
							size="small"
							direction="row"
							:options="circleDropdownOptions"
							@select="handleCircleSelect"
							@click="handleCircleClick"
						/>
					</div>

					<div v-else-if="action.key === 'ellipse'" class="command-split" :class="{ active: isEllipseActive }" :data-sketch-key="action.key">
						<DropdownButton
							:is-active="isEllipseActive"
							:disabled="isSketchActionLockedByMirror(action.key)"
							:icon="activeEllipseOption.icon"
							:hint="{
								label: activeEllipseOption.label,
								image: activeEllipseOption.hintImage,
								desc: activeEllipseOption.hint,
							}"
							size="small"
							direction="row"
							:options="ellipseDropdownOptions"
							@select="handleEllipseSelect"
							@click="handleEllipseClick"
						/>
					</div>

					<div v-else-if="action.key === 'slot'" class="command-split" :class="{ active: isSlotActive }" :data-sketch-key="action.key">
						<DropdownButton
							:is-active="isSlotActive"
							:disabled="isSketchActionLockedByMirror(action.key)"
							:icon="activeSlotOption.icon"
							:hint="{
								label: activeSlotOption.label,
								image: activeSlotOption.hintImage,
								desc: activeSlotOption.hint,
							}"
							size="small"
							direction="row"
							:options="slotDropdownOptions"
							@select="handleSlotSelect"
							@click="handleSlotClick"
						/>
					</div>

					<div
						v-else-if="action.key === 'sketch-corner'"
						class="command-split"
						:class="{ active: isSketchCornerActive }"
						:data-sketch-key="action.key"
					>
						<DropdownButton
							:is-active="isSketchCornerActive"
							:disabled="isSketchActionLockedByMirror(action.key)"
							:icon="activeSketchCornerOption.icon"
							:hint="{
								label: activeSketchCornerOption.label,
								image: activeSketchCornerOption.hintImage,
								desc: activeSketchCornerOption.hint,
							}"
							size="small"
							direction="row"
							:options="sketchCornerDropdownOptions"
							@select="handleSketchCornerSelect"
							@click="handleSketchCornerClick"
						/>
					</div>

					<div v-else-if="action.key === 'spline'" class="command-split" :class="{ active: isSplineActive }" :data-sketch-key="action.key">
						<DropdownButton
							:is-active="isSplineActive"
							:disabled="isSketchActionLockedByMirror(action.key)"
							:icon="activeSplineOption.icon"
							:hint="{
								label: activeSplineOption.label,
								image: activeSplineOption.hintImage,
								desc: activeSplineOption.hint,
							}"
							size="small"
							direction="row"
							:options="splineDropdownOptions"
							@select="handleSplineSelect"
							@click="handleSplineClick"
						/>
					</div>

					<div
						v-else
						class="command-split"
						:class="{ active: !isSketchActionDisabled(action) && isCommandActive(action.command) }"
						:data-sketch-key="action.key"
						:data-sketch-disabled="isSketchActionDisabled(action) ? '1' : '0'"
					>
						<DropdownButton
							:is-active="!isSketchActionDisabled(action) && isCommandActive(action.command)"
							:disabled="isSketchActionDisabled(action)"
							:icon="action.icon"
							:label="isDrawIconOnlyAction(action.key) ? '' : action.label"
							:hint="{
								label: action.label,
								image: action.hintImage,
								desc: action.hint,
							}"
							size="small"
							direction="row"
							@click="handleAction(action)"
						/>
					</div>
				</template>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { computed, h, ref, watch, onBeforeUnmount, type CSSProperties } from "vue";
	import { storeToRefs } from "pinia";
	import type { DropdownOption } from "naive-ui";
	import DropdownButton from "@/components/common/DropdownButton.vue";
	import SvgIcon from "@/components/common/SvgIcon.vue";
	import { useToolbarCommandStore } from "@/stores/toolbar-command";
	import arcCenterIcon from "@/assets/icon/arc_center.svg?raw";
	import arcTangentIcon from "@/assets/icon/arc_tangent.svg?raw";
	import arcThreePointIcon from "@/assets/icon/arc_3point.svg?raw";
	import bezierIcon from "@/assets/icon/bezier.svg?raw";
	import chamferIcon from "@/assets/icon/chamfer.svg?raw";
	import circleCenterIcon from "@/assets/icon/circle_center.svg?raw";
	import circleThreePointIcon from "@/assets/icon/circle_3point.svg?raw";
	import ellipseIcon from "@/assets/icon/ellipse.svg?raw";
	import ellipsePartialIcon from "@/assets/icon/ellipse_partial.svg?raw";
	import ellipseParabolaIcon from "@/assets/icon/ellipse_parabola.svg?raw";
	import filletIcon from "@/assets/icon/fillet.svg?raw";
	import sketchIcon from "@/assets/icon/sketch.svg?raw";
	import sketchExitIcon from "@/assets/icon/sketch-exit.svg?raw";
	import intelligentSizeIcon from "@/assets/icon/intelligent_size.svg?raw";
	import lineIcon from "@/assets/icon/line.svg?raw";
	import lineCenterIcon from "@/assets/icon/line_center.svg?raw";
	import lineMidpointIcon from "@/assets/icon/line_midpoint.svg?raw";
	import polygonIcon from "@/assets/icon/polygon.svg?raw";
	import rectangleCornerIcon from "@/assets/icon/rectangle.svg?raw";
	import rectangleCenterIcon from "@/assets/icon/rectangle_center.svg?raw";
	import rectangleThreePointCornerIcon from "@/assets/icon/rectangle_3point_corner.svg?raw";
	import rectangleThreePointCenterIcon from "@/assets/icon/rectangle_3point_center.svg?raw";
	import rectangleParallelogramIcon from "@/assets/icon/rectangle_parallelogram.svg?raw";
	import slotStraightIcon from "@/assets/icon/slot_straight.svg?raw";
	import slotCenterStraightIcon from "@/assets/icon/slot_center_straight.svg?raw";
	import slotArc3PointIcon from "@/assets/icon/slot_arc_3point.svg?raw";
	import slotArcCenterIcon from "@/assets/icon/slot_arc_center.svg?raw";
	import splineIcon from "@/assets/icon/spline.svg?raw";
	import sketchTrimEntitiesIcon from "@/assets/icon/sketch_trim_entities.svg?raw";
	import sketchExtendEntitiesIcon from "@/assets/icon/sketch_extend_entities.svg?raw";
	import sketchConvertEntitiesIcon from "@/assets/icon/sketch_convert_entities.svg?raw";
	import sketchSilhouetteEntitiesIcon from "@/assets/icon/sketch_silhouette_entities.svg?raw";
	import sketchIntersectionCurveIcon from "@/assets/icon/sketch_intersection_curve.svg?raw";
	import sketchOffsetEntitiesIcon from "@/assets/icon/sketch_offset_entities.svg?raw";
	import sketchOffsetOnSurfaceIcon from "@/assets/icon/sketch_offset_on_surface.svg?raw";
	import sketchMirrorEntitiesIcon from "@/assets/icon/sketch_mirror_entities.svg?raw";
	import sketchLinearPatternIcon from "@/assets/icon/sketch_linear_pattern.svg?raw";
	import sketchCircularPatternIcon from "@/assets/icon/sketch_circular_pattern.svg?raw";
	import sketchMoveEntitiesIcon from "@/assets/icon/sketch_move_entities.svg?raw";
	import sketchCopyEntitiesIcon from "@/assets/icon/sketch_copy_entities.svg?raw";
	import sketchRotateEntitiesIcon from "@/assets/icon/sketch_rotate_entities.svg?raw";
	import sketchScaleEntitiesIcon from "@/assets/icon/sketch_scale_entities.svg?raw";
	import sketchStretchEntitiesIcon from "@/assets/icon/sketch_stretch_entities.svg?raw";
	import sketchDisplayDeleteRelationsIcon from "@/assets/icon/sketch_display_delete_relations.svg?raw";
	import sketchAddRelationsIcon from "@/assets/icon/sketch_add_relations.svg?raw";
	import sketchFullyDefineSketchIcon from "@/assets/icon/sketch_fully_define_sketch.svg?raw";
	import sketchRepairSketchIcon from "@/assets/icon/sketch_repair_sketch.svg?raw";
	import sketchQuickSnapIcon from "@/assets/icon/sketch_quick_snap.svg?raw";
	import sketchQuickSketchIcon from "@/assets/icon/sketch_quick_sketch.svg?raw";
	import sketchInstant2DIcon from "@/assets/icon/sketch_instant2d.svg?raw";
	import sketchShadedSketchContoursIcon from "@/assets/icon/sketch_shaded_sketch_contours.svg?raw";
	import sketchSnapPointIcon from "@/assets/icon/sketch_snap_point_sw.svg?raw";
	import sketchSnapCenterIcon from "@/assets/icon/sketch_snap_center.svg?raw";
	import sketchSnapMidpointIcon from "@/assets/icon/sketch_snap_midpoint.svg?raw";
	import sketchSnapQuadrantIcon from "@/assets/icon/sketch_snap_quadrant.svg?raw";
	import sketchSnapIntersectionIcon from "@/assets/icon/sketch_snap_intersection.svg?raw";
	import sketchSnapNearestIcon from "@/assets/icon/sketch_snap_nearest.svg?raw";
	import sketchSnapTangentIcon from "@/assets/icon/sketch_snap_tangent.svg?raw";
	import sketchSnapHvIcon from "@/assets/icon/sketch_snap_hv.svg?raw";
	import sketchSnapGridIcon from "@/assets/icon/sketch_snap_grid.svg?raw";
	import pointIcon from "@/assets/icon/point.svg?raw";
	import { SketchArcCommand } from "@puppycad/cad-core";
	import { SketchCircleCommand } from "@puppycad/cad-core";
	import { SketchEllipseCommand } from "@puppycad/cad-core";
	import { SketchLineCommand } from "@puppycad/cad-core";
	import { SketchPointCommand } from "@puppycad/cad-core";
	import { SketchPolygonCommand } from "@puppycad/cad-core";
	import { SketchRectangleCommand } from "@puppycad/cad-core";
	import { SketchSlotCommand } from "@puppycad/cad-core";
	import { SketchSplineCommand } from "@puppycad/cad-core";
	import { SketchCornerMode } from "@/core";
	import { useViewer } from "@/hooks/useViewer";
	import { useSketchStateStore } from "@/hooks/useSketchState";
	import { QuickSnapMode } from "@puppycad/cad-core";
	import type { Subscription } from "rxjs";

	/** 尺寸命令家族标识 */
	const SMART_DIMENSION_FAMILY_COMMANDS = [
		"sketch-smart-dimension",
		"sketch-horizontal-dimension",
		"sketch-vertical-dimension",
		"sketch-path-length-dimension",
		"sketch-chain-dimension",
		"sketch-baseline-dimension",
		"sketch-ordinate-dimension",
		"sketch-horizontal-ordinate-dimension",
		"sketch-vertical-ordinate-dimension",
	];

	//------ 快速捕捉运行时状态 开始------
	/** 获取 viewer 单例（用于直接写入快速捕捉状态）。 */
	const { getViewer } = useViewer();

	/** quickSnapModeChange$ 订阅句柄，组件卸载时取消订阅。 */
	let quickSnapModeChangeSub: Subscription | null = null;

	/**
	 * 确保 quickSnapModeChange$ 监听只注册一次。
	 * 说明：在第一次选择快速捕捉时调用，此后多次调用无效。
	 * one-shot 完成后 PointSnapManager 派发 null，此处清除 UI 选中状态，
	 * 由于 sketchLayoutActiveMode 是计算属性 sketchLayoutOptionsMap 的依赖，
	 * 状态变更后图标高亮会自动更新。
	 */
	function ensureQuickSnapListener(): void {
		if (quickSnapModeChangeSub !== null) return;
		const viewer = getViewer();
		if (!viewer?.observeQuickSnapModeChange) return;
		const unsubscribe = viewer.observeQuickSnapModeChange(mode => {
			if (mode === null) {
				// one-shot 完成或手动关闭：清除工具栏选中状态。
				delete sketchLayoutActiveMode.value["quick-snap"];
			} else {
				// 外部更新：同步选中状态。
				sketchLayoutActiveMode.value["quick-snap"] = mode;
			}
		});
		quickSnapModeChangeSub = {
			unsubscribe: () => {
				unsubscribe();
			},
		} as Subscription;
	}

	onBeforeUnmount(() => {
		quickSnapModeChangeSub?.unsubscribe();
		quickSnapModeChangeSub = null;
	});
	//------ 快速捕捉运行时状态 结束------

	interface ToolbarOperation {
		name: string;
		hint: string;
	}

	interface ToolbarAction {
		key: string;
		label: string;
		icon: string;
		command?: string;
		hint?: string;
		hintImage?: string;
		disabled?: boolean;
		/** 仅在草图编辑态可点击；非草图态时按钮自动置灰。 */
		requireSketchContext?: boolean;
	}

	interface ToolbarCategory {
		title: string;
		actions: ToolbarAction[];
		sketchLayout?: SketchLayout;
	}

	type SketchLayoutCellSize = "small" | "large";
	type SketchLayoutCellMode = "row" | "column";

	interface SketchLayoutCell {
		key: string;
		column: number;
		row: number;
		rowSpan?: number;
		size?: SketchLayoutCellSize;
		mode?: SketchLayoutCellMode;
	}

	interface SketchLayout {
		columns: number;
		rows: number;
		cells: SketchLayoutCell[];
	}

	interface SketchLayoutItem {
		action: ToolbarAction;
		column: number;
		row: number;
		rowSpan: number;
		size: SketchLayoutCellSize;
		mode: SketchLayoutCellMode;
	}

	interface RectangleModeOption {
		label: string;
		icon: string;
		command: SketchRectangleCommand;
		hint: string;
		hintImage?: string;
	}

	interface CircleModeOption {
		label: string;
		icon: string;
		command: SketchCircleCommand;
		hint: string;
		hintImage?: string;
	}

	interface ArcModeOption {
		label: string;
		icon: string;
		command: SketchArcCommand;
		hint: string;
		hintImage?: string;
	}

	interface EllipseModeOption {
		label: string;
		icon: string;
		command: SketchEllipseCommand;
		hint: string;
		hintImage?: string;
		disabled?: boolean;
	}

	interface SlotModeOption {
		label: string;
		icon: string;
		command: SketchSlotCommand;
		hint: string;
		hintImage?: string;
	}

	interface SketchCornerModeOption {
		label: string;
		icon: string;
		command: SketchCornerMode;
		hint: string;
		hintImage?: string;
	}

	interface LineModeOption {
		label: string;
		icon: string;
		command: SketchLineCommand;
		hint: string;
		hintImage?: string;
	}

	interface SplineModeOption {
		label: string;
		icon: string;
		command: SketchSplineCommand;
		hint: string;
		hintImage?: string;
	}

	interface SketchLayoutDropdownModeOption {
		mode: string;
		label: string;
		icon: string;
		/** 派发的命令字符串。quick-snap 条目不需要命令，为可选。 */
		command?: string;
		hint: string;
		hintImage?: string;
		disabled?: boolean;
	}

	const props = withDefaults(
		defineProps<{
			currentDrawingTool?: string | null;
			isSketchPlaneSelected?: boolean;
			shadedSketchContoursEnabled?: boolean;
		}>(),
		{
			currentDrawingTool: null,
			isSketchPlaneSelected: false,
			shadedSketchContoursEnabled: false,
		}
	);

	const emit = defineEmits<{
		(e: "command", commandType: string): void;
		(e: "operationChange", operation: ToolbarOperation): void;
	}>();

	const toolbarCommandStore = useToolbarCommandStore();
	const { activeCommands } = storeToRefs(toolbarCommandStore);
	const sketchStateStore = useSketchStateStore();
	const { isSketchActive: isSketchActiveComputed } = storeToRefs(sketchStateStore);
	/** 镜向实体命令是否正在占用草图交互。 */
	const isSketchMirrorEntitiesActive = computed(() => activeCommands.value.includes("sketch-mirror-entities"));
	/**
	 * 草图入口按钮激活态。
	 *
	 * 设计说明：
	 * 1. 草图会话已激活时按钮必须高亮；
	 * 2. 尚未选平面但已进入 `sketch-enter` 待选状态时，也应高亮草图入口；
	 * 3. 退出按钮是否显示仍只取决于真实草图会话，避免待选状态误显示“退出草图”。
	 */
	const isSketchEntryButtonActive = computed(() => isSketchActiveComputed.value || activeCommands.value.includes("sketch-enter"));

	const activeRectangleCommand = ref<SketchRectangleCommand>(SketchRectangleCommand.Corner);
	const activeLineCommand = ref<SketchLineCommand>(SketchLineCommand.Line);
	const activeCircleCommand = ref<SketchCircleCommand>(SketchCircleCommand.CenterRadius);
	const activeArcCommand = ref<SketchArcCommand>(SketchArcCommand.CenterStartEnd);
	const activeEllipseCommand = ref<SketchEllipseCommand>(SketchEllipseCommand.Ellipse);
	const activeSlotCommand = ref<SketchSlotCommand>(SketchSlotCommand.Straight);
	const activeSketchCornerCommand = ref<SketchCornerMode>(SketchCornerMode.Fillet);
	const activeSplineCommand = ref<SketchSplineCommand>(SketchSplineCommand.Spline);

	const rectangleModeOptions: RectangleModeOption[] = [
		{
			label: "角点矩形",
			icon: rectangleCornerIcon,
			command: SketchRectangleCommand.Corner,
			hint: "单击放置矩形的第一个角点，然后拖动矩形，当矩形的大小和形状正确时再次单击绘制矩形。",
			hintImage: "/images/operation/sketch-point-rect.gif",
		},
		{
			label: "中心矩形",
			icon: rectangleCenterIcon,
			command: SketchRectangleCommand.Center,
			hint: "单击放置矩形中心点，然后拖动矩形，当矩形的大小和形状正确时再次单击绘制矩形。",
			hintImage: "/images/operation/sketch-center-point-rect.gif",
		},
		{
			label: "三点角矩形",
			icon: rectangleThreePointCornerIcon,
			command: SketchRectangleCommand.ThreePointCorner,
			hint: "依次点击相邻两个角点，然后拖动矩形，当矩形的大小和形状正确时再次单击绘制矩形。",
			hintImage: "/images/operation/sketch-three-point-rect.gif",
		},
		{
			label: "三点中心矩形",
			icon: rectangleThreePointCenterIcon,
			command: SketchRectangleCommand.ThreePointCenter,
			hint: "单击放置矩形中心点，第二个点确认矩形中心到一条边的距离，最后拖动矩形，当矩形的大小和形状正确时再次单击绘制矩形。",
			hintImage: "/images/operation/sketch-three-point-center-rect.gif",
		},
		{
			label: "平行四边形",
			icon: rectangleParallelogramIcon,
			command: SketchRectangleCommand.Parallelogram,
			hint: "依次点击相邻两个角点，然后拖动平行四边形，当平行四边形的大小和形状正确时再次单击绘制平行四边形。",
			hintImage: "/images/operation/sketch-parallelogram.gif",
		},
	];

	const lineModeOptions: LineModeOption[] = [
		{
			label: "直线",
			icon: lineIcon,
			command: SketchLineCommand.Line,
			hint: "单击放置起点，然后拖动直线，当直线长度正确时再次单击绘制直线，继续拖动点击进行连续绘制（双击结束）",
			hintImage: "/images/operation/sketch-line.gif",
		},
		{
			label: "中心线",
			icon: lineCenterIcon,
			command: SketchLineCommand.CenterLine,
			hint: "单击放置起点，然后拖动虚直线，当虚直线长度正确时再次单击绘制虚直线，继续拖动点击进行连续绘制（双击结束）",
			hintImage: "/images/operation/sketch-dotted-line.gif",
		},
		{
			label: "中点线",
			icon: lineMidpointIcon,
			command: SketchLineCommand.MidpointLine,
			hint: "单击放置直线中心点，然后拖动直线，当直线长度正确时再次单击绘制直线，继续拖动点击进行连续绘制（双击结束）",
			hintImage: "/images/operation/sketch-center-line.gif",
		},
	];

	const splineModeOptions: SplineModeOption[] = [
		{
			label: "样条曲线",
			icon: splineIcon,
			command: SketchSplineCommand.Spline,
			hint: "单击并拖动以创建每段样条曲线。双击完成样条曲线（穿过所有控制点）",
			hintImage: "/images/operation/sketch-spline.gif",
		},
		{
			label: "样式样条曲线",
			icon: bezierIcon,
			command: SketchSplineCommand.StyleSpline,
			hint: "单击并拖动以创建每段样式样条曲线。双击完成样式样条曲线（不穿过中间控制点）",
			hintImage: "/images/operation/sketch-bezier.gif",
		},
	];

	const circleModeOptions: CircleModeOption[] = [
		{
			label: "圆",
			icon: circleCenterIcon,
			command: SketchCircleCommand.CenterRadius,
			hint: "单击放置圆心，然后拖动圆，当圆形半径正确时再次单击绘制圆。",
			hintImage: "/images/operation/sketch-center-circle.gif",
		},
		{
			label: "周边圆",
			icon: circleThreePointIcon,
			command: SketchCircleCommand.ThreePoint,
			hint: "使用两次单击在圆周上放置两个点。然后移动鼠标改变圆，使用第三次单击来确定绘制周边圆",
			hintImage: "/images/operation/sketch-three-point-circle.gif",
		},
	];

	const arcModeOptions: ArcModeOption[] = [
		{
			label: "圆心/起点/终点画弧",
			icon: arcCenterIcon,
			command: SketchArcCommand.CenterStartEnd,
			hint: "单击放置圆弧中心点，二次单击确定半径，第三次单击确定弧长度。",
			hintImage: "/images/operation/sketch-center-start-end-arc.gif",
		},
		{
			label: "切线弧",
			icon: arcTangentIcon,
			command: SketchArcCommand.Tangent,
			hint: "选择草图实体的终点，然后拖动以定义切线弧。",
			hintImage: "/images/operation/sketch-tangent-arc.gif",
		},
		{
			label: "3点圆弧",
			icon: arcThreePointIcon,
			command: SketchArcCommand.ThreePoint,
			hint: "使用两次单击来放置弧起点和终点，然后使用第三次单击来完成圆弧绘制。",
			hintImage: "/images/operation/sketch-three-point-arc.gif",
		},
	];

	const ellipseModeOptions: EllipseModeOption[] = [
		{
			label: "椭圆",
			icon: ellipseIcon,
			command: SketchEllipseCommand.Ellipse,
			hint: "单击放置中心点，然后通过两次单击来确定椭圆的长轴端点和短轴端点完成绘制。",
			hintImage: "/images/operation/sketch-ellipse.gif",
		},
		{
			label: "部分椭圆",
			icon: ellipsePartialIcon,
			command: SketchEllipseCommand.Partial,
			hint: "单击放置中心点，然后通过两次单击来确定椭圆的长轴端点和短轴端点，最后移动鼠标位置，确认部分椭圆的弧长时进行第四次单击完成绘制。",
			hintImage: "/images/operation/sketch-partial-ellipse.gif",
		},
		{
			label: "抛物线",
			icon: ellipseParabolaIcon,
			command: SketchEllipseCommand.Parabola,
			hint: "前两次单击进行抛物线定形，然后再选择起点与终点以完成绘制。",
			hintImage: "/images/operation/sketch-parabola-ellipse.gif",
		},
	];

	const slotModeOptions: SlotModeOption[] = [
		{
			label: "直槽口",
			icon: slotStraightIcon,
			command: SketchSlotCommand.Straight,
			hint: "使用三次单击，先放置起点，接着定义长度，然后定义槽口的宽度。",
			hintImage: "/images/operation/sketch-straight-slot.gif",
		},
		{
			label: "中心点直槽口",
			icon: slotCenterStraightIcon,
			command: SketchSlotCommand.CenterStraight,
			hint: "使用三次单击，先放置中心点，接着定义长度，然后定义槽口的宽度。",
			hintImage: "/images/operation/sketch-center-straight-slot.gif",
		},
		{
			label: "三点圆弧槽口",
			icon: slotArc3PointIcon,
			command: SketchSlotCommand.ThreePointArc,
			hint: "使用三次单击来放置起点、终点和第三点以定义弧的形状。然后使用第四次单击来定义槽口的宽度。",
			hintImage: "/images/operation/sketch-three-point-arc-slot.gif",
		},
		{
			label: "中心点圆弧槽口",
			icon: slotArcCenterIcon,
			command: SketchSlotCommand.CenterArc,
			hint: "第一次单击放置中心点，第二次单击定义圆弧的半径。第三次单击定义圆弧的长度。最后移动鼠标并在确定新增后单击以定义槽口的宽度。",
			hintImage: "/images/operation/sketch-center-arc-slot.gif",
		},
	];

	//------ 直线圆弧草图圆角提示 开始------
	const sketchCornerModeOptions: SketchCornerModeOption[] = [
		{
			label: "圆角",
			icon: filletIcon,
			command: SketchCornerMode.Fillet,
			hint: "将两个草图实体相交处的边角圆角化，以创建一条切线弧。您可以选择两个草图实体或选择一个边角。如果您选择非相交实体，则实体将延伸到带倒角边角的虚拟交叉点。",
			hintImage: "/images/operation/sketch-fillet.gif",
		},
		{
			label: "倒角",
			icon: chamferIcon,
			command: SketchCornerMode.Chamfer,
			hint: "将两个草图实体相交处的边角倒角化。您可以选择两个草图实体或选择一个边角。如果您选择非相交实体，则实体将延伸到带倒角边角的虚拟交叉点。",
			hintImage: "/images/operation/sketch-chamfer.gif",
		},
	];
	//------ 直线圆弧草图圆角提示 结束------

	const sketchCategories: ToolbarCategory[] = [
		{
			title: "绘制",
			actions: [
				{ key: "line", label: "直线", icon: lineIcon, command: SketchLineCommand.Line, hint: "点击起点，连续点击绘制线段（双击结束）" },
				{ key: "rectangle", label: "矩形", icon: rectangleCornerIcon, command: SketchRectangleCommand.Corner, hint: "绘制直角矩形" },
				{ key: "circle", label: "圆", icon: circleCenterIcon, command: SketchCircleCommand.CenterRadius, hint: "点击圆心，再点击边缘点绘制圆" },
				{ key: "arc", label: "圆弧", icon: arcCenterIcon, command: SketchArcCommand.CenterStartEnd, hint: "以三点定义圆弧" },
				{ key: "ellipse", label: "椭圆", icon: ellipseIcon, command: SketchEllipseCommand.Ellipse, hint: "以中心与半径绘制椭圆" },
				{
					key: "slot",
					label: "槽口",
					icon: slotStraightIcon,
					command: SketchSlotCommand.Straight,
					hint: "绘制直槽口、中心点直槽口、三点圆弧槽口或中心点圆弧槽口",
				},
				//------ 直线圆弧草图圆角提示 开始------
				{
					key: "sketch-corner",
					label: "圆角/倒角",
					icon: filletIcon,
					command: SketchCornerMode.Fillet,
					hint: "草图圆角支持实体对或共享角点；倒角支持两条相邻直线或其共享角点",
				},
				//------ 直线圆弧草图圆角提示 结束------
				{
					key: "polygon",
					label: "多边形",
					icon: polygonIcon,
					command: SketchPolygonCommand.Polygon,
					hint: "单击放置中心点，然后拖动并释放以确定多边形的大小和方向。绘制多边形后，可以更改边数",
					hintImage: "/images/operation/sketch-polygon.gif",
				},
				{ key: "spline", label: "样条曲线", icon: splineIcon, command: SketchSplineCommand.Spline, hint: "样条曲线（穿过所有控制点）" },
				{ key: "point", label: "点", icon: pointIcon, command: SketchPointCommand.Point, hint: "点击放置一个点" },
			],
		},
		{
			title: "草图编辑",
			actions: [
				{
					key: "trim-entities",
					label: "剪裁实体",
					icon: sketchTrimEntitiesIcon,
					command: "sketch-trim-entities",
					hint: "剪裁实体：支持强劲剪裁、角点、在内/在外剪除与剪裁到最近端",
					hintImage: "/images/operation/sketch-trim-entities.gif",
				},
				{
					key: "convert-entities",
					label: "转换实体引用",
					icon: sketchConvertEntitiesIcon,
					command: "sketch-convert-entities",
					hint: "将所选边线和草图实体转换为相同实体，方法是将其投影到草图平面或面上。",
					hintImage: "/images/operation/sketch-convert-entities.gif",
				},
				{
					key: "offset-entities",
					label: "等距实体",
					icon: sketchOffsetEntitiesIcon,
					command: "sketch-offset-entities",
					hint: "通过指定距离偏移一个或多个草图实体。例如，创建从选定直线偏移的直线。如果原始实体发生改变，则当您重建模型时，偏移实体也会改变。",
					requireSketchContext: true,
					hintImage: "/images/operation/sketch-offset-entities.gif",
				},
				{
					key: "offset-on-surface",
					label: "曲面上偏移",
					icon: sketchOffsetOnSurfaceIcon,
					command: "sketch-offset-on-surface",
					hint: "将曲面边界边线偏移到曲面模型上并在其上创建3D草",
					disabled: true,
				},
			],
			sketchLayout: {
				columns: 4,
				rows: 2,
				cells: [
					{ key: "trim-entities", column: 1, row: 1, rowSpan: 2, size: "large", mode: "column" },
					{ key: "convert-entities", column: 2, row: 1, rowSpan: 2, size: "large", mode: "column" },
					{ key: "offset-entities", column: 3, row: 1, rowSpan: 2, size: "large", mode: "column" },
					{ key: "offset-on-surface", column: 4, row: 1, rowSpan: 2, size: "large", mode: "column" },
				],
			},
		},
		{
			title: "镜向与阵列",
			actions: [
				{
					key: "mirror-entities",
					label: "镜向实体",
					icon: sketchMirrorEntitiesIcon,
					command: "sketch-mirror-entities",
					hint: "基于中心线或平面参考来镜向所选实体。",
					requireSketchContext: true,
				},
				{
					key: "linear-sketch-pattern",
					label: "圆周草图阵列",
					icon: sketchCircularPatternIcon,
					command: "sketch-circular-pattern",
					hint: "添加草图实体的阵列",
				},
				//------ 草图工具栏移动实体入口 开始------
				{
					key: "move-entities",
					label: "移动实体",
					icon: sketchMoveEntitiesIcon,
					command: "sketch-move-entities",
					hint: "移动草图实体。",
				},
				//------ 草图工具栏移动实体入口 结束------
			],
			sketchLayout: {
				columns: 1,
				rows: 3,
				cells: [
					{ key: "mirror-entities", column: 1, row: 1, size: "small", mode: "row" },
					{ key: "linear-sketch-pattern", column: 1, row: 2, size: "small", mode: "row" },
					{ key: "move-entities", column: 1, row: 3, size: "small", mode: "row" },
				],
			},
		},
		{
			title: "关系与辅助",
			actions: [
				{
					key: "display-delete-relations",
					label: "显示/删除几何关系",
					icon: sketchDisplayDeleteRelationsIcon,
					command: "sketch-display-delete-relations",
					hint: "显示和删除几何关系",
				},
				{
					key: "repair-sketch",
					label: "修复草图",
					icon: sketchRepairSketchIcon,
					command: "sketch-repair",
					hint: "查找失败的草图实体，例如较小的实体、重叠的线或弧，以及在三个或更多实体之间共享的点。",
				},
				{
					key: "quick-snap",
					label: "快捷捕捉",
					icon: sketchQuickSnapIcon,
					command: "sketch-quick-snap",
					hint: "快捷捕捉过滤器。",
				},
				{
					key: "quick-sketch",
					label: "快速草图",
					icon: sketchQuickSketchIcon,
					command: "sketch-quick-sketch",
					hint: "选择平坦表面或平面，并使用任何草图工具开始绘制草图。移动到另一个平面时，会创建一个平面并打开一个草图。您可以中途更换草图工具。",
				},
				{
					key: "instant2d",
					label: "Instant2D",
					icon: sketchInstant2DIcon,
					command: "sketch-instant2d",
					hint: "通过拖动尺寸手柄，启用草图的动态修改。屏幕上的标尺可帮助您精确测量修改。",
				},
				{
					key: "shaded-sketch-contours",
					label: "上色草图轮廓",
					icon: sketchShadedSketchContoursIcon,
					command: "sketch-shaded-sketch-contours",
					hint: "启用上色草图轮廓。使用上色草图轮廓将闭合的草图轮廓和子轮廓视为上色实体。",
				},
			],
			sketchLayout: {
				columns: 6,
				rows: 2,
				cells: [
					{ key: "display-delete-relations", column: 1, row: 1, rowSpan: 2, size: "large", mode: "column" },
					{ key: "repair-sketch", column: 2, row: 1, rowSpan: 2, size: "large", mode: "column" },
					{ key: "quick-snap", column: 3, row: 1, rowSpan: 2, size: "large", mode: "column" },
					{ key: "quick-sketch", column: 4, row: 1, rowSpan: 2, size: "large", mode: "column" },
					{ key: "instant2d", column: 5, row: 1, rowSpan: 2, size: "large", mode: "column" },
					{ key: "shaded-sketch-contours", column: 6, row: 1, rowSpan: 2, size: "large", mode: "column" },
				],
			},
		},
	];

	/**
	 * 镜向实体激活时需要锁定的前序草图按钮。
	 *
	 * PuppyCAD 为适配窄屏把移动族排在镜向按钮下方，但在 SOLIDWORKS 命令顺序中
	 * 它仍位于镜向实体之前，因此这里按命令族语义锁定，不能按当前 DOM 坐标推导。
	 */
	const sketchMirrorPrecedingActionKeys = new Set([
		...sketchCategories
			.filter(category => category.title === "绘制" || category.title === "草图编辑")
			.flatMap(category => category.actions.map(action => action.key)),
		"linear-sketch-pattern",
		"move-entities",
	]);

	const sketchLayoutDropdownConfig: Record<string, SketchLayoutDropdownModeOption[]> = {
		"trim-entities": [
			{
				mode: "trim-entities",
				label: "剪裁实体(T)",
				icon: sketchTrimEntitiesIcon,
				command: "sketch-trim-entities",
				hint: "强劲剪裁：按住左键拖动连续修剪，Shift+拖动可延伸，回拖红方块可撤销一步",
			},
			{
				mode: "extend-entities",
				label: "延伸实体",
				icon: sketchExtendEntitiesIcon,
				command: "sketch-extend-entities",
				hint: "延伸实体：在强劲剪裁中按 Shift 拖动延伸",
			},
		],
		"convert-entities": [
			{ mode: "convert-entities", label: "转换实体引用", icon: sketchConvertEntitiesIcon, command: "sketch-convert-entities", hint: "转换实体引用" },
			{
				mode: "silhouette-entities",
				label: "侧影实体",
				icon: sketchSilhouetteEntitiesIcon,
				command: "sketch-silhouette-entities",
				hint: "侧影实体（待实现）",
			},
			{
				mode: "intersection-curve",
				label: "交叉曲线",
				icon: sketchIntersectionCurveIcon,
				command: "sketch-intersection-curve",
				hint: "交叉曲线（待实现）",
			},
		],
		"linear-sketch-pattern": [
			{
				mode: "sketch-linear-pattern",
				label: "线性草图阵列",
				icon: sketchLinearPatternIcon,
				command: "sketch-linear-pattern",
				hint: "线性草图阵列（待实现）",
				disabled: true,
			},
			{
				mode: "sketch-circular-pattern",
				label: "圆周草图阵列",
				icon: sketchCircularPatternIcon,
				command: "sketch-circular-pattern",
				hint: "圆周草图阵列",
			},
		],
		"move-entities": [
			{
				mode: "move-entities",
				label: "移动实体",
				icon: sketchMoveEntitiesIcon,
				command: "sketch-move-entities",
				hint: "移动实体：选择草图实体并设置 From/To 或 X/Y 参数",
			},
			{
				mode: "copy-entities",
				label: "复制实体",
				icon: sketchCopyEntitiesIcon,
				command: "sketch-copy-entities",
				hint: "复制实体：选择草图实体并设置复制位移参数",
			},
			{
				mode: "rotate-entities",
				label: "旋转实体",
				icon: sketchRotateEntitiesIcon,
				command: "sketch-rotate-entities",
				hint: "旋转实体：选择草图实体、旋转中心和角度",
			},
			{ mode: "scale-entities", label: "缩放实体比例", icon: sketchScaleEntitiesIcon, command: "sketch-scale-entities", hint: "缩放实体比例（待实现）" },
			{ mode: "stretch-entities", label: "伸展实体", icon: sketchStretchEntitiesIcon, command: "sketch-stretch-entities", hint: "伸展实体（待实现）" },
		],
		"display-delete-relations": [
			{
				mode: "display-delete-relations",
				label: "显示/删除几何关系",
				icon: sketchDisplayDeleteRelationsIcon,
				command: "sketch-display-delete-relations",
				hint: "显示/删除几何关系（待实现）",
			},
			{ mode: "add-relations", label: "添加几何关系", icon: sketchAddRelationsIcon, command: "sketch-add-relations", hint: "添加几何关系（待实现）" },
			{
				mode: "fully-define-sketch",
				label: "完全定义草图",
				icon: sketchFullyDefineSketchIcon,
				command: "sketch-fully-define",
				hint: "完全定义草图（待实现）",
			},
		],
		"quick-snap": [
			{ mode: QuickSnapMode.Point, label: "点捕捉", icon: sketchSnapPointIcon, hint: "点捕捉" },
			{ mode: QuickSnapMode.Center, label: "中心点捕捉", icon: sketchSnapCenterIcon, hint: "中心点捕捉" },
			{ mode: QuickSnapMode.Midpoint, label: "中点捕捉", icon: sketchSnapMidpointIcon, hint: "中点捕捉" },
			{ mode: QuickSnapMode.Quadrant, label: "象限点", icon: sketchSnapQuadrantIcon, hint: "象限点捕捉" },
			{ mode: QuickSnapMode.Intersection, label: "交叉点捕捉", icon: sketchSnapIntersectionIcon, hint: "交叉点捕捉" },
			{ mode: QuickSnapMode.Nearest, label: "最近端捕捉", icon: sketchSnapNearestIcon, hint: "最近端捕捉" },
			{ mode: QuickSnapMode.Tangent, label: "相切捕捉", icon: sketchSnapTangentIcon, hint: "相切捕捉" },
			{ mode: QuickSnapMode.HorizontalVertical, label: "H/V 点捕捉", icon: sketchSnapHvIcon, hint: "H/V 点捕捉" },
			{ mode: QuickSnapMode.Grid, label: "网格捕捉", icon: sketchSnapGridIcon, hint: "网格捕捉" },
		],
	};

	const sketchLayoutActiveMode = ref<Record<string, string>>({
		"trim-entities": "trim-entities",
		"convert-entities": "convert-entities",
		"linear-sketch-pattern": "sketch-circular-pattern",
		"move-entities": "move-entities",
		"display-delete-relations": "display-delete-relations",
	});

	function getSketchLayoutModeOptions(actionKey: string): SketchLayoutDropdownModeOption[] {
		return sketchLayoutDropdownConfig[actionKey] ?? [];
	}

	function getSketchLayoutActiveOption(action: ToolbarAction): SketchLayoutDropdownModeOption | null {
		const options = getSketchLayoutModeOptions(action.key);
		if (options.length === 0) {
			return null;
		}
		// quick-snap 无强制默认选中项，仅返回当前激活的选项（可能为 null）。
		if (action.key === "quick-snap") {
			const activeMode = sketchLayoutActiveMode.value["quick-snap"];
			return options.find(option => option.mode === activeMode) ?? null;
		}
		const activeMode = sketchLayoutActiveMode.value[action.key] ?? options[0].mode;
		return options.find(option => option.mode === activeMode) ?? options[0];
	}

	/**
	 * 计算各 actionKey 对应的下拉选项列表。
	 * 说明：以计算属性形式实现，确保 sketchLayoutActiveMode 变化时自动重计算，
	 * 解决 icon 渲染函数内无法追踪 Vue 响应式依赖的问题。
	 */
	const sketchLayoutOptionsMap = computed<Record<string, DropdownOption[]>>(() => {
		// 在计算属性上下文中读取选中状态，Vue 可正确追踪依赖。
		const activeQuickSnapMode = sketchLayoutActiveMode.value["quick-snap"];

		const result: Record<string, DropdownOption[]> = {};
		for (const actionKey of Object.keys(sketchLayoutDropdownConfig)) {
			result[actionKey] = sketchLayoutDropdownConfig[actionKey].map(option => ({
				key: option.mode,
				label: option.label,
				disabled: option.disabled,
				icon: () =>
					h(SvgIcon, {
						content: option.icon,
						alt: option.label,
						class: [
							"dropdown-option-icon",
							// quick-snap 选中时添加高亮 class（使用外层已追踪的快照值）。
							actionKey === "quick-snap" && activeQuickSnapMode === option.mode ? "dropdown-option-icon--active" : "",
						],
					}),
			}));
		}
		return result;
	});

	function getSketchLayoutLabel(action: ToolbarAction): string {
		// quick-snap 始终显示固定标签，不随选中项变化。
		if (action.key === "quick-snap") {
			return action.label;
		}
		const activeOption = getSketchLayoutActiveOption(action);
		return activeOption?.label ?? action.label;
	}

	function getSketchLayoutIcon(action: ToolbarAction): string {
		// quick-snap 始终显示固定图标，不随选中项变化。
		if (action.key === "quick-snap") {
			return action.icon;
		}
		const activeOption = getSketchLayoutActiveOption(action);
		return activeOption?.icon ?? action.icon;
	}

	function isSketchLayoutDisabled(action: ToolbarAction): boolean {
		if (isSketchActionLockedByMirror(action.key)) {
			return true;
		}
		// 标记了 requireSketchContext 的按钮，非草图编辑态下禁用
		if (action.requireSketchContext && !isSketchActiveComputed.value) {
			return true;
		}
		const activeOption = getSketchLayoutActiveOption(action);
		return Boolean(action.disabled || activeOption?.disabled);
	}

	/**
	 * 判断草图按钮是否被镜向实体独占命令锁定。
	 *
	 * @param actionKey 草图按钮标识。
	 * @returns 镜向实体激活且按钮位于其前方时返回 true。
	 */
	function isSketchActionLockedByMirror(actionKey: string): boolean {
		return isSketchMirrorEntitiesActive.value && sketchMirrorPrecedingActionKeys.has(actionKey);
	}

	/**
	 * 判断普通草图按钮是否不可用。
	 *
	 * @param action 草图按钮配置。
	 * @returns 按钮不可用时返回 true。
	 */
	function isSketchActionDisabled(action: ToolbarAction): boolean {
		return Boolean(action.disabled) || isSketchActionLockedByMirror(action.key);
	}

	function isSketchLayoutActive(action: ToolbarAction): boolean {
		if (action.key === "shaded-sketch-contours") {
			return Boolean(props.shadedSketchContoursEnabled);
		}
		const options = getSketchLayoutModeOptions(action.key);
		if (options.length === 0) {
			return !action.disabled && isCommandActive(action.command);
		}
		return !action.disabled && options.some(option => isCommandActive(option.command));
	}

	function handleSketchLayoutSelect(actionKey: string, mode: string): void {
		const action = sketchCategories.flatMap(category => category.actions).find(candidate => candidate.key === actionKey);
		if (!action || isSketchActionDisabled(action)) {
			return;
		}
		const options = getSketchLayoutModeOptions(actionKey);
		const selected = options.find(option => option.mode === mode);
		if (!selected) {
			return;
		}
		sketchLayoutActiveMode.value[actionKey] = selected.mode;
		if (selected.disabled) {
			return;
		}

		//------ 快速捕捉运行时状态 开始------
		// quick-snap 子菜单直接写入 PointSnapManager 运行时状态，不再派发命令。
		if (actionKey === "quick-snap") {
			const viewer = getViewer();
			if (viewer?.setQuickSnapMode && viewer?.getQuickSnapMode) {
				// 首次选择时注册 one-shot 监听。
				ensureQuickSnapListener();
				viewer.setQuickSnapMode(selected.mode as QuickSnapMode);
				// 同步 UI 勾选状态：再次点击同一项则取消勾选。
				const newActive = viewer.getQuickSnapMode();
				if (newActive === null) {
					delete sketchLayoutActiveMode.value["quick-snap"];
				} else {
					sketchLayoutActiveMode.value["quick-snap"] = newActive;
				}
			}
			emitOperation(selected.label, selected.hint);
			return;
		}
		//------ 快速捕捉运行时状态 结束------

		if (selected.command) {
			emit("command", selected.command);
		}
		emitOperation(selected.label, selected.hint);
	}

	function handleSketchLayoutClick(action: ToolbarAction): void {
		if (isSketchActionDisabled(action)) {
			return;
		}

		//------ 快速捕捉运行时状态 开始------
		// quick-snap 主按钮点击：只在有激活选项时才 toggle，否则不派发命令（等用户从下拉菜单选择）。
		if (action.key === "quick-snap") {
			const activeOption = getSketchLayoutActiveOption(action);
			if (activeOption) {
				const viewer = getViewer();
				if (viewer?.setQuickSnapMode && viewer?.getQuickSnapMode) {
					// 首次点击时注册 one-shot 监听。
					ensureQuickSnapListener();
					viewer.setQuickSnapMode(activeOption.mode as QuickSnapMode);
					const newActive = viewer.getQuickSnapMode();
					if (newActive === null) {
						delete sketchLayoutActiveMode.value["quick-snap"];
					} else {
						sketchLayoutActiveMode.value["quick-snap"] = newActive;
					}
				}
				emitOperation(activeOption.label, activeOption.hint);
			}
			return;
		}
		//------ 快速捕捉运行时状态 结束------

		const activeOption = getSketchLayoutActiveOption(action);
		if (activeOption) {
			if (activeOption.disabled) {
				return;
			}
			if (activeOption.command) {
				emit("command", activeOption.command);
			}
			emitOperation(activeOption.label, activeOption.hint);
			return;
		}
		handleAction(action);
	}

	function hasSketchLayout(category: ToolbarCategory): category is ToolbarCategory & { sketchLayout: SketchLayout } {
		return Boolean(category.sketchLayout && category.sketchLayout.cells.length > 0);
	}

	function getSketchLayoutItems(category: ToolbarCategory): SketchLayoutItem[] {
		if (!hasSketchLayout(category)) {
			return [];
		}
		return category.sketchLayout.cells
			.map(cell => {
				const action = category.actions.find(candidate => candidate.key === cell.key);
				if (!action) {
					return null;
				}
				return {
					action,
					column: cell.column,
					row: cell.row,
					rowSpan: cell.rowSpan ?? 1,
					size: cell.size ?? "small",
					mode: cell.mode ?? "column",
				} satisfies SketchLayoutItem;
			})
			.filter((item): item is SketchLayoutItem => item !== null);
	}

	function getSketchGridStyle(category: ToolbarCategory): CSSProperties {
		if (!hasSketchLayout(category)) {
			return {};
		}
		return {
			"--sketch-grid-columns": String(category.sketchLayout.columns),
			"--sketch-grid-rows": String(category.sketchLayout.rows),
		} as CSSProperties;
	}

	function getSketchCellStyle(item: SketchLayoutItem): CSSProperties {
		return {
			gridColumn: String(item.column),
			gridRow: `${item.row} / span ${item.rowSpan}`,
		};
	}

	const rectangleCommandMap = new Map(rectangleModeOptions.map(option => [option.command, option]));
	const activeRectangleOption = computed(() => rectangleCommandMap.get(activeRectangleCommand.value) ?? rectangleModeOptions[0]);
	const isRectangleActive = computed(() => {
		const hasActive = activeCommands.value.some(command => Object.values(SketchRectangleCommand).includes(command as SketchRectangleCommand));
		return hasActive || props.currentDrawingTool === "rectangle";
	});
	const rectangleDropdownOptions = computed<DropdownOption[]>(() =>
		rectangleModeOptions.map(option => ({
			key: option.command,
			label: option.label,
			icon: () => h(SvgIcon, { content: option.icon, alt: option.label, class: "dropdown-option-icon" }),
		}))
	);

	const lineCommandMap = new Map(lineModeOptions.map(option => [option.command, option]));
	const activeLineOption = computed(() => lineCommandMap.get(activeLineCommand.value) ?? lineModeOptions[0]);
	const isLineActive = computed(() => {
		const hasActive = activeCommands.value.some(command => Object.values(SketchLineCommand).includes(command as SketchLineCommand));
		return hasActive || props.currentDrawingTool === "line";
	});
	const lineDropdownOptions = computed<DropdownOption[]>(() =>
		lineModeOptions.map(option => ({
			key: option.command,
			label: option.label,
			icon: () => h(SvgIcon, { content: option.icon, alt: option.label, class: "dropdown-option-icon" }),
		}))
	);

	const splineCommandMap = new Map(splineModeOptions.map(option => [option.command, option]));
	const activeSplineOption = computed(() => splineCommandMap.get(activeSplineCommand.value) ?? splineModeOptions[0]);
	const isSplineActive = computed(() => {
		const hasActive = activeCommands.value.some(command => Object.values(SketchSplineCommand).includes(command as SketchSplineCommand));
		return hasActive || props.currentDrawingTool === "spline" || props.currentDrawingTool === "style_spline" || props.currentDrawingTool === "bezier";
	});
	const splineDropdownOptions = computed<DropdownOption[]>(() =>
		splineModeOptions.map(option => ({
			key: option.command,
			label: option.label,
			icon: () => h(SvgIcon, { content: option.icon, alt: option.label, class: "dropdown-option-icon" }),
		}))
	);

	const circleCommandMap = new Map(circleModeOptions.map(option => [option.command, option]));
	const activeCircleOption = computed(() => circleCommandMap.get(activeCircleCommand.value) ?? circleModeOptions[0]);
	const isCircleActive = computed(() => {
		const hasActive = activeCommands.value.some(command => Object.values(SketchCircleCommand).includes(command as SketchCircleCommand));
		return hasActive || props.currentDrawingTool === "circle";
	});
	const circleDropdownOptions = computed<DropdownOption[]>(() =>
		circleModeOptions.map(option => ({
			key: option.command,
			label: option.label,
			icon: () => h(SvgIcon, { content: option.icon, alt: option.label, class: "dropdown-option-icon" }),
		}))
	);

	const arcCommandMap = new Map(arcModeOptions.map(option => [option.command, option]));
	const activeArcOption = computed(() => arcCommandMap.get(activeArcCommand.value) ?? arcModeOptions[0]);
	const isArcActive = computed(() => {
		const hasActive = activeCommands.value.some(command => Object.values(SketchArcCommand).includes(command as SketchArcCommand));
		return hasActive || props.currentDrawingTool === "arc";
	});
	const arcDropdownOptions = computed<DropdownOption[]>(() =>
		arcModeOptions.map(option => ({
			key: option.command,
			label: option.label,
			icon: () => h(SvgIcon, { content: option.icon, alt: option.label, class: "dropdown-option-icon" }),
		}))
	);

	const ellipseCommandMap = new Map(ellipseModeOptions.map(option => [option.command, option]));
	const activeEllipseOption = computed(() => ellipseCommandMap.get(activeEllipseCommand.value) ?? ellipseModeOptions[0]);
	const isEllipseActive = computed(() => {
		const hasActive = activeCommands.value.some(command => Object.values(SketchEllipseCommand).includes(command as SketchEllipseCommand));
		return hasActive || props.currentDrawingTool === "ellipse";
	});
	const ellipseDropdownOptions = computed<DropdownOption[]>(() =>
		ellipseModeOptions.map(option => ({
			key: option.command,
			label: option.label,
			disabled: option.disabled,
			icon: () => h(SvgIcon, { content: option.icon, alt: option.label, class: "dropdown-option-icon" }),
		}))
	);

	const slotCommandMap = new Map(slotModeOptions.map(option => [option.command, option]));
	const activeSlotOption = computed(() => slotCommandMap.get(activeSlotCommand.value) ?? slotModeOptions[0]);
	const isSlotActive = computed(() => {
		const hasActive = activeCommands.value.some(command => Object.values(SketchSlotCommand).includes(command as SketchSlotCommand));
		return hasActive || props.currentDrawingTool === "slot";
	});
	const slotDropdownOptions = computed<DropdownOption[]>(() =>
		slotModeOptions.map(option => ({
			key: option.command,
			label: option.label,
			icon: () => h(SvgIcon, { content: option.icon, alt: option.label, class: "dropdown-option-icon" }),
		}))
	);

	const sketchCornerCommandMap = new Map(sketchCornerModeOptions.map(option => [option.command, option]));
	const activeSketchCornerOption = computed(() => sketchCornerCommandMap.get(activeSketchCornerCommand.value) ?? sketchCornerModeOptions[0]);
	const isSketchCornerActive = computed(() =>
		activeCommands.value.some(
			command => command === "fillet" || command === "chamfer" || command === SketchCornerMode.Fillet || command === SketchCornerMode.Chamfer
		)
	);
	const sketchCornerDropdownOptions = computed<DropdownOption[]>(() =>
		sketchCornerModeOptions.map(option => ({
			key: option.command,
			label: option.label,
			icon: () => h(SvgIcon, { content: option.icon, alt: option.label, class: "dropdown-option-icon" }),
		}))
	);

	watch(
		activeCommands,
		commands => {
			if (commands.includes("chamfer") || commands.includes(SketchCornerMode.Chamfer)) {
				activeSketchCornerCommand.value = SketchCornerMode.Chamfer;
				return;
			}
			if (commands.includes("fillet") || commands.includes(SketchCornerMode.Fillet)) {
				activeSketchCornerCommand.value = SketchCornerMode.Fillet;
			}
		},
		{ deep: true }
	);

	function emitOperation(name: string, hint: string): void {
		emit("operationChange", { name, hint });
	}

	const drawIconOnlyActionKeys = new Set(["line", "rectangle", "circle", "arc", "ellipse", "slot", "sketch-corner", "polygon", "spline", "point"]);

	function isDrawIconOnlyAction(actionKey: string): boolean {
		return drawIconOnlyActionKeys.has(actionKey);
	}

	function isCommandActive(command?: string): boolean {
		if (!command) return false;
		return activeCommands.value.includes(command);
	}

	function handleSketchMenuSelect(key: string): void {
		if (isSketchMirrorEntitiesActive.value) {
			return;
		}
		if (key === "sketch-enter") {
			emit("command", key);
		}
	}

	function handleSketchEntryClick(): void {
		if (isSketchMirrorEntitiesActive.value) {
			return;
		}
		emit("command", "sketch-enter");
	}

	function applyLineCommand(command: SketchLineCommand): void {
		const option = lineCommandMap.get(command);
		if (!option) return;
		activeLineCommand.value = command;
		emit("command", option.command);
		emitOperation(option.label, option.hint);
	}

	function handleLineSelect(key: string): void {
		applyLineCommand(key as SketchLineCommand);
	}
	function handleLineClick(): void {
		applyLineCommand(activeLineOption.value.command);
	}

	function applyRectangleCommand(command: SketchRectangleCommand): void {
		const option = rectangleCommandMap.get(command);
		if (!option) return;
		activeRectangleCommand.value = command;
		emit("command", option.command);
		emitOperation(option.label, option.hint);
	}

	function handleRectangleSelect(key: string): void {
		applyRectangleCommand(key as SketchRectangleCommand);
	}
	function handleRectangleClick(): void {
		applyRectangleCommand(activeRectangleOption.value.command);
	}

	function applyArcCommand(command: SketchArcCommand): void {
		const option = arcCommandMap.get(command);
		if (!option) return;
		activeArcCommand.value = command;
		emit("command", option.command);
		emitOperation(option.label, option.hint);
	}

	function handleArcSelect(key: string): void {
		applyArcCommand(key as SketchArcCommand);
	}
	function handleArcClick(): void {
		applyArcCommand(activeArcOption.value.command);
	}

	function applyCircleCommand(command: SketchCircleCommand): void {
		const option = circleCommandMap.get(command);
		if (!option) return;
		activeCircleCommand.value = command;
		emit("command", option.command);
		emitOperation(option.label, option.hint);
	}

	function handleCircleSelect(key: string): void {
		applyCircleCommand(key as SketchCircleCommand);
	}
	function handleCircleClick(): void {
		applyCircleCommand(activeCircleOption.value.command);
	}

	function applyEllipseCommand(command: SketchEllipseCommand): void {
		const option = ellipseCommandMap.get(command);
		if (!option || option.disabled) return;
		activeEllipseCommand.value = command;
		emit("command", option.command);
		emitOperation(option.label, option.hint);
	}

	function handleEllipseSelect(key: string): void {
		applyEllipseCommand(key as SketchEllipseCommand);
	}
	function handleEllipseClick(): void {
		applyEllipseCommand(activeEllipseOption.value.command);
	}

	function applySlotCommand(command: SketchSlotCommand): void {
		const option = slotCommandMap.get(command);
		if (!option) return;
		activeSlotCommand.value = command;
		emit("command", option.command);
		emitOperation(option.label, option.hint);
	}

	function handleSlotSelect(key: string): void {
		applySlotCommand(key as SketchSlotCommand);
	}
	function handleSlotClick(): void {
		applySlotCommand(activeSlotOption.value.command);
	}

	function applySketchCornerCommand(command: SketchCornerMode): void {
		const option = sketchCornerCommandMap.get(command);
		if (!option) return;
		activeSketchCornerCommand.value = command;
		emit("command", option.command);
		emitOperation(option.label, option.hint);
	}

	function handleSketchCornerSelect(key: string): void {
		applySketchCornerCommand(key as SketchCornerMode);
	}
	function handleSketchCornerClick(): void {
		applySketchCornerCommand(activeSketchCornerOption.value.command);
	}

	function applySplineCommand(command: SketchSplineCommand): void {
		const option = splineCommandMap.get(command);
		if (!option) return;
		activeSplineCommand.value = command;
		emit("command", option.command);
		emitOperation(option.label, option.hint);
	}

	function handleSplineSelect(key: string): void {
		applySplineCommand(key as SketchSplineCommand);
	}
	function handleSplineClick(): void {
		applySplineCommand(activeSplineOption.value.command);
	}

	function handleAction(action: ToolbarAction): void {
		if (isSketchActionDisabled(action)) {
			return;
		}
		if (action.command) {
			emit("command", action.command);
		}
		emitOperation(action.label, action.hint ?? "正在打开操作，请在视口中选择相关对象。");
	}
</script>

<style scoped lang="less">
	.command-categories {
		display: flex;
		align-items: stretch;
		gap: 0;
		flex-wrap: nowrap;
		width: 100%;
		height: 116px;
		min-height: 116px;
		max-height: 116px;
		box-sizing: border-box;
		overflow-x: auto;
	}

	.category {
		display: flex;
		flex-direction: column;
		gap: 6px;
		padding: 4px 8px;
		min-width: max-content;
		position: relative;
	}

	.category + .category {
		border-left: 1px solid var(--n-tab-border-color);
	}

	.category-actions {
		display: flex;
		align-items: center;
		gap: 4px;
		flex-wrap: wrap;
	}

	.category-actions.sketch-layout-grid {
		--sketch-large-width: 72px;
		--sketch-small-min-width: 76px;
		--sketch-row-height: 34px;
		display: grid;
		grid-template-columns: repeat(var(--sketch-grid-columns), max-content);
		grid-template-rows: repeat(var(--sketch-grid-rows), var(--sketch-row-height));
		align-items: stretch;
		justify-items: stretch;
		column-gap: 3px;
		row-gap: 3px;
	}

	.sketch-command {
		width: max-content;
		height: 100%;
	}

	.sketch-command-small {
		width: max-content;
		min-width: var(--sketch-small-min-width);
	}

	.sketch-command-large {
		width: var(--sketch-large-width);
	}

	/**
 * 以下三个按钮的文案超过 6 字或含斜杠 / 括号，72px 默认宽度会在中文字符间折断出现
 * "转换实体引 / 月"、"显示 / 删除几何关" 等奇怪截断；按标签真实长度放宽到 100–140px。
 * 仅覆盖这 3 个 key，不扩全局 `--sketch-large-width`，避免其他已对齐格子变形。
 */
	.sketch-command-large[data-sketch-key="trim-entities"],
	.sketch-command-large[data-sketch-key="convert-entities"] {
		width: 100px;
	}

	.sketch-command-large[data-sketch-key="display-delete-relations"] {
		width: 140px;
	}

	.sketch-command > * {
		width: 100%;
	}

	.command-split {
		display: inline-flex;
		align-items: stretch;
		gap: 2px;
	}

	.category-draw .category-actions {
		display: grid;
		grid-auto-flow: column;
		grid-template-rows: repeat(3, auto);
		align-items: start;
	}

	:deep(.dropdown-option-icon) {
		width: 18px;
		height: 18px;
		object-fit: contain;
	}
</style>
